import React from "react";
import { IrctcAddressChangeFormKeys } from "@swiggy-private/cart-types";

import {
    EditRow,
    EditContainer,
    TextContainer,
    EditCta,
    TextLabel,
    StyledSeparator,
    HeaderTextContainer,
    ContainerV2,
} from "./IrctcAddressChange.style";
import { Image, useEffectOnce } from "@swiggy-private/core-ui";
import { getCloudinaryUrl } from "@swiggy-private/core-ui";
import {
    DATA_TESTID,
    EDIT_CTA_TEXT,
    IRCTC_TEXTS,
    IRCTC_ICONS,
    IRCTC_DELIVERY_LOCATION_TEXT_V2,
    IRCTC_DELIVERY_LOCATION_TEXT,
    DELIVERY_AT_STATION_TEXT,
} from "./constants";
import { VerticalStack, HorizontalStack } from "@swiggy-private/dls-web-layouts/dist/components/Stack";
import { useTheme } from "@swiggy-private/dls-web-theme/dist/components/ThemeProvider/use-theme";
import { Separator } from "@swiggy-private/dls-web-layouts/dist/components/Separator";
import { format, isEmpty } from "@swiggy-private/js-utils";
import { IrctcAddressChangeProps } from "./type";
import { fullCaseToTitleCase } from "./helpers";

const IrctcAddressChange: React.FC<IrctcAddressChangeProps> = ({
    irctcData,
    trainOrderAttributes,
    editHandler,
    onImpressionIrctcUpdateAddress,
    selectedAddress,
    hideEditCta,
}) => {
    useEffectOnce(() => {
        onImpressionIrctcUpdateAddress?.();
    });

    const theme = useTheme();
    const hasMobileNumber = !isEmpty(irctcData.mobile_number);

    return (
        <ContainerV2>
            {!isEmpty(selectedAddress) ? (
                <>
                    <HorizontalStack alignItems="start">
                        <Image width={20} imageUrl={getCloudinaryUrl(IRCTC_ICONS.V2.HEADER)} alt=""></Image>
                        <HeaderTextContainer>
                            <TextLabel variant="V2.body2.neutral.medium" color="text_High_Emphasis" isV2>
                                {selectedAddress?.sla_message?.replace(
                                    IRCTC_DELIVERY_LOCATION_TEXT,
                                    IRCTC_DELIVERY_LOCATION_TEXT_V2,
                                ) ?? ""}
                            </TextLabel>
                            <TextLabel variant="V2.body2.neutral.regular" color="text_Low_Emphasis" isV2>
                                {format(DELIVERY_AT_STATION_TEXT, fullCaseToTitleCase(irctcData.station_name ?? ""))}
                            </TextLabel>
                        </HeaderTextContainer>
                    </HorizontalStack>

                    <StyledSeparator>
                        <Separator width="100%" height={1} color={theme.colors[theme.colorMode].border_Tertiary} />
                    </StyledSeparator>
                </>
            ) : null}
            <EditContainer>
                <EditRow isV2>
                    <Image width={20} imageUrl={getCloudinaryUrl(IRCTC_ICONS.V2.BERTH)} alt=""></Image>
                    <TextContainer isV2>
                        <VerticalStack>
                            <TextLabel
                                variant="V2.body2.neutral.medium"
                                color="text_High_Emphasis"
                                data-testid={DATA_TESTID.DELIVERY_LOCALTION}
                                isV2
                            >
                                {IRCTC_TEXTS.V2.DELIVERY_LOCATION_TEXT} {irctcData.coach}/{irctcData.berth}
                            </TextLabel>
                            {!isEmpty(trainOrderAttributes) && (
                                <TextLabel variant="V2.body2.neutral.regular" color="text_Low_Emphasis" isV2>
                                    {`${fullCaseToTitleCase(trainOrderAttributes.train_name)}, ${trainOrderAttributes.train_number}`}
                                </TextLabel>
                            )}
                        </VerticalStack>
                        {!hideEditCta && (
                            <EditCta
                                onClick={editHandler(IrctcAddressChangeFormKeys.COACH_NO)}
                                variant="V2.body2.neutral.bold"
                                color="primary"
                                data-testid={DATA_TESTID.EDIT_DELIVERY_LOCALTION}
                                data-label={EDIT_CTA_TEXT}
                            >
                                <Image width={28} imageUrl={getCloudinaryUrl(IRCTC_ICONS.V2.EDIT_ICON)} alt=""></Image>
                            </EditCta>
                        )}
                    </TextContainer>
                </EditRow>
                {hasMobileNumber && (
                    <>
                        <StyledSeparator>
                            <Separator width="100%" height={1} color={theme.colors[theme.colorMode].border_Tertiary} />
                        </StyledSeparator>
                        <EditRow isV2>
                            <Image width={20} imageUrl={getCloudinaryUrl(IRCTC_ICONS.V2.PHONE)} alt=""></Image>
                            <TextContainer removeBottomBorder isV2>
                                <VerticalStack>
                                    <TextLabel
                                        variant="V2.body2.neutral.medium"
                                        color="text_High_Emphasis"
                                        data-testid={DATA_TESTID.CONTACT_NUMBER}
                                        isV2
                                    >
                                        {IRCTC_TEXTS.V2.MOBILE_NUMBER_TEXT} {irctcData.mobile_number}
                                    </TextLabel>
                                    <TextLabel variant="V2.body2.neutral.regular" color="text_Low_Emphasis" isV2>
                                        {IRCTC_TEXTS.V2.MOBILE_NUMBER_SUBTEXT}
                                    </TextLabel>
                                </VerticalStack>
                                {!hideEditCta && (
                                    <EditCta
                                        onClick={editHandler(IrctcAddressChangeFormKeys.CONTACT_NO)}
                                        variant="V2.body2.neutral.bold"
                                        color="primary"
                                        data-testid={DATA_TESTID.EDIT_CONTACT_NUMBER}
                                        data-label={EDIT_CTA_TEXT}
                                    >
                                        <Image width={28} imageUrl={getCloudinaryUrl(IRCTC_ICONS.V2.EDIT_ICON)} alt=""></Image>
                                    </EditCta>
                                )}
                            </TextContainer>
                        </EditRow>
                    </>
                )}
            </EditContainer>
        </ContainerV2>
    );
};

export default React.memo(IrctcAddressChange);
